import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { Mail, Send, FileText, History, AlertTriangle, RefreshCw, Eye, Save, Beaker } from 'lucide-react';
import { toast } from 'sonner';

const AUDIENCE_OPTIONS = [
  { value: 'contacts_all', label: 'All contacts + all app users (deduplicated)' },
  { value: 'users_all', label: 'All app users' },
  { value: 'users_beta', label: 'Beta users only' },
  { value: 'users_free', label: 'Free users only' },
  { value: 'users_paid', label: 'Paid users only' },
  { value: 'users_alpaca_connected', label: 'Users with Alpaca connected' },
  { value: 'users_alpaca_not_connected', label: 'Users without Alpaca' },
  { value: 'users_invited_not_verified', label: 'Invited but not verified' },
  { value: 'custom_emails', label: 'Custom email list' },
];

const EMAIL_TYPES = ['invite', 'announcement', 'marketing', 'transactional'];

export default function AdminEmail() {
  const [tab, setTab] = useState('compose');
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [testingConn, setTestingConn] = useState(false);

  const reloadStatus = useCallback(() => {
    api.get('/admin/email/status').then(r => setSmtpStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => { reloadStatus(); }, [reloadStatus]);

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const { data } = await api.post('/admin/email/test-connection');
      const labelMap = {
        smtp_not_configured: 'SMTP not configured — set env vars first.',
        smtp_auth_failed: 'SMTP auth failed — check SMTP_USERNAME / SMTP_PASSWORD (Gmail app password).',
        smtp_send_failed: 'SMTP connection failed — check SMTP_HOST / SMTP_PORT / SMTP_USE_TLS.',
        smtp_sent_successfully: 'SMTP connection OK.',
      };
      const msg = labelMap[data.error_label] || `Result: ${data.error_label || 'unknown'}`;
      if (data.ok) toast.success(msg);
      else toast.error(msg);
      reloadStatus();
    } catch {
      toast.error('Could not test SMTP connection.');
    } finally {
      setTestingConn(false);
    }
  };

  return (
    <AdminLayout title="Email Center" description="Send customizable one-by-one emails — no group emails, no CC/BCC, recipient list never exposed. Drafts and templates work even when SMTP isn't configured.">
      {smtpStatus && !smtpStatus.smtp_configured && (
        <div className="mb-6 border border-amber-500/30 bg-amber-500/[0.04] rounded-md p-4 flex items-start gap-3" data-testid="email-smtp-warning">
          <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-200">SMTP is not configured</p>
            <p className="mt-1 text-xs text-amber-200/70 leading-relaxed">
              Missing env vars: <code className="font-mono text-amber-300">{(smtpStatus.missing_fields || []).join(', ') || '—'}</code>. Sent emails log as <code className="font-mono text-amber-300">skipped</code> until these are set.
            </p>
          </div>
          <button
            onClick={testConnection}
            disabled={testingConn}
            data-testid="email-test-connection-btn"
            className="text-xs px-3 py-2 border border-amber-400/40 text-amber-200 hover:bg-amber-400/[0.08] rounded-sm font-mono disabled:opacity-50"
          >
            {testingConn ? 'Testing…' : 'Test SMTP'}
          </button>
        </div>
      )}
      {smtpStatus && smtpStatus.smtp_configured && (
        <div className="mb-6 border border-emerald-500/25 bg-emerald-500/[0.04] rounded-md p-4 flex items-start gap-3" data-testid="email-smtp-ok">
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-200">SMTP configured</p>
            <p className="mt-1 text-xs text-emerald-200/70 leading-relaxed">
              Host: <code className="font-mono">{smtpStatus.host}</code> · From: <code className="font-mono">{smtpStatus.from_email}</code>
              {smtpStatus.reply_to ? <> · Reply-To: <code className="font-mono">{smtpStatus.reply_to}</code></> : null}
              {smtpStatus.last_test ? <> · Last test: <span className="font-mono">{smtpStatus.last_test.error_label || smtpStatus.last_test.status}</span></> : null}
            </p>
          </div>
          <button
            onClick={testConnection}
            disabled={testingConn}
            data-testid="email-test-connection-btn"
            className="text-xs px-3 py-2 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/[0.08] rounded-sm font-mono disabled:opacity-50"
          >
            {testingConn ? 'Testing…' : 'Test SMTP'}
          </button>
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-6 border-b border-white/5" data-testid="email-tabs">
        {[
          { k: 'compose', label: 'Compose', icon: Mail },
          { k: 'templates', label: 'Templates', icon: FileText },
          { k: 'logs', label: 'Logs', icon: History },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`email-tab-${t.k}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                tab === t.k ? 'border-[#d8b34a] text-white' : 'border-transparent text-zinc-500 hover:text-zinc-200'
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'compose' && <ComposeTab smtpConfigured={smtpStatus?.smtp_configured} />}
      {tab === 'templates' && <TemplatesTab onUseTemplate={() => setTab('compose')} />}
      {tab === 'logs' && <LogsTab />}
    </AdminLayout>
  );
}

function ComposeTab({ smtpConfigured }) {
  const [campaignName, setCampaignName] = useState('');
  const [emailType, setEmailType] = useState('announcement');
  const [audienceKind, setAudienceKind] = useState('contacts_all');
  const [customEmails, setCustomEmails] = useState('');
  const [subject, setSubject] = useState('Hi {{first_name}}, this is Talas');
  const [body, setBody] = useState('Hi {{first_name}},\n\nQuick note from Talas Trading Co.\n\n{{unsubscribe_link_text}}');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get('/admin/email/templates').then(r => setTemplates(r.data.items || [])).catch(() => {});
  }, []);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setBody(t.text_body);
    setEmailType(t.email_type || 'announcement');
    toast.success(`Loaded template: ${t.name}`);
  };

  const buildAudience = () => {
    if (audienceKind === 'custom_emails') {
      const emails = customEmails.split(/[,\s\n;]+/).map(e => e.trim()).filter(Boolean);
      return { kind: 'custom_emails', emails };
    }
    return { kind: audienceKind };
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/email/test', {
        subject, text_body: body, to: testTo || undefined, email_type: emailType,
      });
      if (data.result?.status === 'sent') toast.success(`Test sent to ${data.to}`);
      else if (data.result?.status === 'skipped') toast.warning(`Logged but skipped (${data.result.error})`);
      else toast.error(`Test failed: ${data.result?.error}`);
    } catch (e) { toast.error('Test send failed.'); }
    finally { setBusy(false); }
  };

  const handleSend = async () => {
    if (!campaignName.trim()) { toast.error('Campaign name required.'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body required.'); return; }
    if (!window.confirm(`Send "${campaignName}" to ${audienceKind}? Emails go out one at a time.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/email/send', {
        campaign_name: campaignName, email_type: emailType,
        audience: buildAudience(), subject, text_body: body,
      });
      toast.success(`Queued ${data.queued_count}. Skipped ${data.skipped_count}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    } finally { setBusy(false); }
  };

  const handleSaveTemplate = async () => {
    const name = window.prompt('Template name?');
    if (!name) return;
    try {
      await api.post('/admin/email/templates', { name, email_type: emailType, subject, text_body: body });
      toast.success('Template saved.');
      const r = await api.get('/admin/email/templates');
      setTemplates(r.data.items || []);
    } catch (e) { toast.error('Save failed.'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="CAMPAIGN NAME" testid="field-campaign-name">
            <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} data-testid="email-campaign-name" className={inputCls} placeholder="e.g. beta-invite-wave-1" />
          </FieldLabel>
          <FieldLabel label="EMAIL TYPE" testid="field-email-type">
            <select value={emailType} onChange={(e) => setEmailType(e.target.value)} data-testid="email-type-select" className={inputCls}>
              {EMAIL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </FieldLabel>
        </div>

        <FieldLabel label="AUDIENCE">
          <select value={audienceKind} onChange={(e) => setAudienceKind(e.target.value)} data-testid="email-audience-select" className={inputCls}>
            {AUDIENCE_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </FieldLabel>
        {audienceKind === 'custom_emails' && (
          <FieldLabel label="CUSTOM EMAIL LIST (comma or newline-separated)">
            <textarea value={customEmails} onChange={(e) => setCustomEmails(e.target.value)} data-testid="email-custom-list" rows={3} className={`${inputCls} font-mono resize-none`} placeholder="alice@example.com, bob@example.com" />
          </FieldLabel>
        )}

        <FieldLabel label="SUBJECT">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" className={inputCls} />
        </FieldLabel>
        <FieldLabel label="BODY (plain text — supports {{first_name}}, {{email}}, {{magic_link}}, {{unsubscribe_link}}, {{unsubscribe_link_text}})">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body" rows={14} className={`${inputCls} font-mono text-xs leading-relaxed resize-none`} />
        </FieldLabel>

        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-white/5">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test to (defaults to your admin email)" data-testid="email-test-to" className={`${inputCls} max-w-xs`} />
          <button onClick={sendTest} disabled={busy} data-testid="email-send-test" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm disabled:opacity-60">
            <Beaker size={13} /> Send test
          </button>
          <button onClick={handleSaveTemplate} data-testid="email-save-template" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm">
            <Save size={13} /> Save as template
          </button>
          <button onClick={handleSend} disabled={busy} data-testid="email-send-campaign" className="ml-auto inline-flex items-center gap-2 text-sm px-5 py-2.5 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm font-medium disabled:opacity-60">
            <Send size={13} /> {busy ? 'Sending…' : 'Send campaign'}
          </button>
        </div>
      </div>

      {/* Preview / templates sidebar */}
      <aside className="space-y-5">
        <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Eye size={13} className="gold-text" />
            <p className="text-xs tracking-[0.3em] font-mono gold-text">PREVIEW</p>
          </div>
          <p className="text-xs font-mono text-zinc-500">SUBJECT</p>
          <p className="mt-1 text-sm text-zinc-100">{subject || <em className="text-zinc-600">—</em>}</p>
          <p className="mt-4 text-xs font-mono text-zinc-500">BODY</p>
          <pre className="mt-1 text-xs text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">{body || '—'}</pre>
        </div>

        <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-5">
          <p className="text-xs tracking-[0.3em] font-mono gold-text mb-3">QUICK TEMPLATES</p>
          <ul className="space-y-1.5" data-testid="email-templates-quick-list">
            {templates.slice(0, 8).map(t => (
              <li key={t.id}>
                <button onClick={() => applyTemplate(t)} className="w-full text-left text-xs px-3 py-2 border border-white/5 rounded-sm bg-white/[0.02] hover:border-[#d8b34a]/40 transition-colors">
                  <p className="text-zinc-100 font-medium">{t.name}</p>
                  <p className="text-zinc-500 text-[10px] mt-0.5 truncate">{t.subject}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-5 text-[11px] text-zinc-500 leading-relaxed">
          <p className="text-xs tracking-[0.3em] font-mono gold-text mb-2">SAFETY</p>
          One email per recipient. No CC/BCC. Unsubscribed contacts auto-skipped for marketing/announcement emails. Transactional and invite emails go through regardless.
          {smtpConfigured === false && <p className="mt-2 text-amber-300/80">SMTP missing — sends log as skipped.</p>}
        </div>
      </aside>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#d8b34a]/50';

function FieldLabel({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function TemplatesTab({ onUseTemplate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/email/templates');
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="email-templates-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-400">{loading ? 'Loading…' : `${items.length} templates`}</p>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(t => (
          <div key={t.id} className="border border-white/10 rounded-md bg-[#0c0c0e] p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs tracking-[0.3em] font-mono text-zinc-500">{(t.email_type || '—').toUpperCase()}{t.is_default && <span className="ml-2 gold-text">· DEFAULT</span>}</p>
                <h4 className="mt-1 text-base font-medium text-white">{t.name}</h4>
                <p className="mt-2 text-sm text-zinc-300 truncate">{t.subject}</p>
              </div>
              <button onClick={onUseTemplate} className="text-xs gold-text hover:underline shrink-0">Use →</button>
            </div>
            <pre className="mt-3 text-xs text-zinc-500 font-mono whitespace-pre-wrap leading-relaxed line-clamp-6">{t.text_body}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const { data } = await api.get('/admin/email/logs', { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="email-logs-tab">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-1.5">
          {['', 'sent', 'skipped', 'failed'].map(s => (
            <button key={s || 'all'} onClick={() => setStatus(s)} data-testid={`logs-filter-${s || 'all'}`} className={`text-xs px-3 py-1.5 border rounded-sm font-mono ${status === s ? 'border-[#d8b34a]/50 text-white bg-[#d8b34a]/[0.06]' : 'border-white/10 text-zinc-400 hover:text-white'}`}>
              {(s || 'all').toUpperCase()}
            </button>
          ))}
        </div>
        <p className="text-sm text-zinc-500 font-mono">{loading ? '…' : `${items.length}`}</p>
      </div>
      <div className="border border-white/10 rounded-md overflow-hidden bg-[#0c0c0e]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left text-[10px] tracking-[0.3em] font-mono text-zinc-500">
                <th className="px-4 py-3">CAMPAIGN</th>
                <th className="px-4 py-3">RECIPIENT</th>
                <th className="px-4 py-3">TYPE</th>
                <th className="px-4 py-3">SUBJECT</th>
                <th className="px-4 py-3">STATUS</th>
                <th className="px-4 py-3">DETAIL</th>
                <th className="px-4 py-3">WHEN</th>
              </tr>
            </thead>
            <tbody>
              {items.map(l => (
                <tr key={l.id} className="border-t border-white/5">
                  <td className="px-4 py-3 text-xs text-zinc-200 font-mono">{l.campaign_name}</td>
                  <td className="px-4 py-3 text-xs text-zinc-100">{l.recipient_email}</td>
                  <td className="px-4 py-3 text-[10px] font-mono text-zinc-400">{l.email_type}</td>
                  <td className="px-4 py-3 text-xs text-zinc-300 max-w-[200px] truncate" title={l.subject}>{l.subject}</td>
                  <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                  <td className="px-4 py-3 text-[10px] text-zinc-500 font-mono max-w-[180px] truncate" title={l.error_message || ''}>{l.error_message || '—'}</td>
                  <td className="px-4 py-3 text-[10px] text-zinc-500 font-mono whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500 text-sm">No email logs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tones = {
    sent: 'border-emerald-500/40 text-emerald-400 bg-emerald-500/5',
    skipped: 'border-amber-500/40 text-amber-400 bg-amber-500/5',
    failed: 'border-red-500/40 text-red-400 bg-red-500/5',
    queued: 'border-blue-500/40 text-blue-400',
  };
  return <span className={`text-[10px] font-mono px-2 py-1 border rounded-sm ${tones[status] || 'border-white/10 text-zinc-400'}`}>{(status || '—').toUpperCase()}</span>;
}
